import React from 'react'
import Chart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { Basket } from '../core/types'
import { nf } from '../core/consts'
import { METRIC_HINTS } from '../core/hints'
import { useChartTheme } from '../core/chartTheme'
import { SERIES_COLOR } from '../core/seriesColors'
import InfoHint from '../../../../../common/InfoHint'

type Props = {
  baskets: Basket[]
  onOpenDrilldown: (signal: string, title: string) => void
}

// Labels derived from actual band bounds so they match the currency (a band may
// be 0–50k in one currency and 0–5k in another).
const bandLabel = (b: Basket): string => {
  const f = (n: number) => nf.format(Math.round(n))
  if (b.max <= 1) return '0'
  if (b.min <= 1) return `до ${f(b.max)}`
  if (b.max >= 1e9) return `от ${f(b.min)}`
  return `${f(b.min)}–${f(b.max)}`
}

const BasketsChart: React.FC<Props> = ({ baskets, onOpenDrilldown }) => {
  const theme = useChartTheme()
  const bs = baskets ?? []
  const categories = bs.map(bandLabel)
  const activeSum = bs.reduce((s, b) => s + b.active_payouts, 0)
  const demandSum = bs.reduce((s, b) => s + b.deposit_demand_count, 0)
  const limitSum = bs.reduce((s, b) => s + b.limit, 0)

  const series = [
    { name: 'Выплаты (занято)', type: 'column', data: bs.map((b) => b.active_payouts) },
    { name: 'Спрос депозитов', type: 'column', data: bs.map((b) => b.deposit_demand_count) },
    { name: 'Лимит выплат', type: 'line', data: bs.map((b) => b.limit) },
  ]

  const options: ApexOptions = {
    chart: {
      type: 'line',
      stacked: false,
      toolbar: { show: false },
      animations: { enabled: false },
      background: 'transparent',
    },
    theme: { mode: theme.mode },
    grid: { borderColor: theme.gridColor },
    // Порядок серий: выплаты, депозиты, лимит. Цвета берутся из общего правила
    // дашборда, красный остаётся только линией лимита.
    colors: [SERIES_COLOR.payout, SERIES_COLOR.deposit, '#F1416C'],
    // Limit is a dashed stepped boundary line per basket; demand/payouts are columns.
    stroke: { width: [0, 0, 3], curve: 'stepline', dashArray: [0, 0, 6] },
    plotOptions: { bar: { columnWidth: '55%', borderRadius: 4 } },
    dataLabels: { enabled: false },
    // Тултип оси у Apex полупрозрачный и наезжает на подпись под ним; час и так
    // есть в заголовке основного тултипа.
    xaxis: { categories, tooltip: { enabled: false } },
    yaxis: { title: { text: 'Кол-во' }, min: 0 },
    legend: {
      position: 'top',
      formatter: (name: string) => {
        if (name.startsWith('Выплаты')) return `Выплаты занято (Σ ${nf.format(activeSum)})`
        if (name.startsWith('Спрос')) return `Спрос депозитов (Σ ${nf.format(demandSum)})`
        if (name.startsWith('Лимит')) return `Лимит выплат (Σ ${nf.format(limitSum)})`
        return name
      },
    },
    tooltip: { shared: true, intersect: false },
  }

  return (
    <div className='card h-100'>
      <div className='card-header'>
        <h3 className='card-title d-inline-flex align-items-center'>
          Корзины: выплаты vs депозиты
          <InfoHint text={METRIC_HINTS.baskets_chart} />
        </h3>
      </div>
      <div className='card-body'>
        <div
          onClick={() => onOpenDrilldown('basket_full', 'Активные выплаты по корзинам')}
          style={{ cursor: 'pointer' }}
        >
          <Chart options={options} series={series} type='line' height={300} />
        </div>
      </div>
    </div>
  )
}

export default BasketsChart
